// 📁 File: src/components/ConfirmDialog.js

import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";

/**
 * 🔥 Hàm hiển thị dialog xác nhận (dạng nguy hiểm như XÓA)
 * @param {Object} options - Thông tin hiển thị
 * @param {string} options.title - Tiêu đề cảnh báo
 * @param {string} options.text - Nội dung mô tả
 * @param {string} options.confirmText - Văn bản nút xác nhận (default: "Xác nhận")
 * @param {string} options.cancelText - Văn bản nút hủy (default: "Hủy")
 * @returns {Promise<SweetAlertResult>} - Kết quả chọn của người dùng
 */
export const showConfirmDialog = async ({
  title,
  text,
  confirmText = "Xác nhận",
  cancelText = "Hủy",
  confirmDelaySeconds = 0,
}) => {
  let countdownTimer = null;

  return await Swal.fire({
    title,
    text,
    icon: "warning",
    showCancelButton: true,
    confirmButtonText:
      confirmDelaySeconds > 0 ? `${confirmText} (${confirmDelaySeconds}s)` : confirmText,
    cancelButtonText: cancelText,
    reverseButtons: true,
    buttonsStyling: false,
    didOpen: () => {
      if (!confirmDelaySeconds) return;

      const confirmButton = Swal.getConfirmButton();
      if (!confirmButton) return;

      let remaining = Number(confirmDelaySeconds) || 0;
      confirmButton.disabled = true;
      confirmButton.classList.add("opacity-60", "cursor-not-allowed");

      countdownTimer = window.setInterval(() => {
        remaining -= 1;

        if (remaining > 0) {
          confirmButton.textContent = `${confirmText} (${remaining}s)`;
          return;
        }

        window.clearInterval(countdownTimer);
        countdownTimer = null;
        confirmButton.disabled = false;
        confirmButton.classList.remove("opacity-60", "cursor-not-allowed");
        confirmButton.textContent = confirmText;
      }, 1000);
    },
    willClose: () => {
      if (countdownTimer) {
        window.clearInterval(countdownTimer);
      }
    },
    customClass: {
      container: "!z-[100000]",
      actions: "flex justify-center gap-4 mt-4",
      confirmButton:
        "bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-full",
      cancelButton:
        "bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded-full",
      popup: "rounded-3xl shadow-lg",
    },
  });
};

/**
 * ✅ Hàm hiển thị thông báo thành công ngắn gọn
 * @param {string} message - Nội dung cần hiển thị
 */
export const showSuccessToast = (message) => {
  Swal.fire({
    toast: true,
    position: "top-end",
    icon: "success",
    title: message,
    showConfirmButton: false,
    timer: 2000,
    timerProgressBar: true,
    customClass: { container: "!z-[100000]" },
  });
};

/** Toast thông tin (không phải lỗi) */
export const showInfoToast = (message) => {
  Swal.fire({
    toast: true,
    position: "top-end",
    icon: "info",
    title: message,
    showConfirmButton: false,
    timer: 3500,
    timerProgressBar: true,
    customClass: { container: "!z-[100000]" },
  });
};

/**
 * ❌ Hàm hiển thị thông báo lỗi
 * @param {string} message - Nội dung lỗi cần thông báo
 */
export const showErrorToast = (message) => {
  Swal.fire({
    toast: true,
    position: "top-end",
    icon: "error",
    title: message,
    showConfirmButton: false,
    timer: 2500,
    timerProgressBar: true,
    customClass: { container: "!z-[100000]" },
  });
};

/**
 * ℹ️ Hàm hiển thị hộp thoại thông tin
 * @param {string} title - Tiêu đề
 * @param {string} text - Nội dung
 */
export const showInfoDialog = (title, html) => {
  Swal.fire({
    icon: "info",
    title,
    html,
    confirmButtonText: "Đóng",
    width: 500,
    buttonsStyling: false, // ⚠️ cần false để custom class hoạt động
    customClass: {
      popup: "rounded-lg shadow-lg",
      confirmButton:
        "bg-gray-300 hover:bg-gray-400 text-white font-semibold py-2 px-4 rounded-full",
      actions: "flex justify-end mt-4 rounded-3xl",
    },
  });
};


/**
 * ✏️ Hàm hiển thị input nhận dữ liệu từ người dùng
 * @param {Object} options - Các tuỳ chọn hiển thị
 * @param {string} options.title - Tiêu đề
 * @param {string} options.inputLabel - Nhãn input
 * @param {string} options.confirmText - Nút xác nhận
 * @param {'text'|'textarea'} [options.inputType] - Loại input
 * @param {string} [options.inputPlaceholder]
 * @param {(value: string) => string|null} [options.inputValidator] - Trả chuỗi lỗi hoặc null
 * @returns {Promise<string|null>} - Giá trị người dùng nhập hoặc null nếu huỷ
 */
export const showInputDialog = async ({
  title,
  inputLabel,
  confirmText = "Xác nhận",
  inputType = "text",
  inputPlaceholder = "",
  inputValidator,
}) => {
  const { value, isConfirmed } = await Swal.fire({
    title,
    input: inputType === "textarea" ? "textarea" : "text",
    inputLabel,
    inputPlaceholder,
    showCancelButton: true,
    reverseButtons: false,
    confirmButtonText: confirmText,
    cancelButtonText: "Hủy",
    inputAttributes: {
      autocapitalize: "off",
    },
    inputValidator:
      typeof inputValidator === "function"
        ? inputValidator
        : undefined,
    customClass: {
      popup: "rounded-lg shadow-md",
      confirmButton:
        "bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-full",
      cancelButton:
        "bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded-full",
      actions: "flex justify-end gap-4 mt-4 rounded-3xl",
    },
    buttonsStyling: false,
  });

  return isConfirmed ? value : null;
};


// ⚠️ Hàm hiển thị thông báo cảnh báo
// @param {string} message - Nội dung cảnh báo
// @param {string} position - Vị trí hiển thị (mặc định: "top-end")
// @param {number} timer - Thời gian hiển thị (mặc định: 3000ms)
// @param {boolean} showConfirmButton - Hiển thị nút xác nhận (mặc định: false)
// @param {boolean} timerProgressBar - Hiển thị thanh tiến trình (mặc định: true)
export const showWarningToast = (message) => {
  Swal.fire({
    icon: "warning",
    title: "Cảnh báo",
    text: message,
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    customClass: { container: "!z-[100000]" },
  });
};